/**
 * Shared minimal policy-layout primitives.
 *
 * Used by:
 *   - components/legal/LegalDocument.tsx  (primary consumer)
 *   - pages/security/vulnerability-disclosure-policy.tsx (re-pointed from SecurityLayout)
 *
 * Design language: hardcoded minimal palette (#0a0a0a / #111 / rgba(0,0,0,.NN) / #fafafa),
 * Space Grotesk for labels only, fixed 820px reading column, hairline borders, no shadows,
 * no gradients. Zero VDP-specific strings or logic — pure presentational kit.
 */
import React from 'react';
import styles from './PolicyLayout.module.css';

function policyPrimitive<
  E extends HTMLElement,
  P extends React.HTMLAttributes<E>,
>(tag: string, moduleClass: string, displayName: string) {
  const Component = React.forwardRef<E, P>((props, ref) => {
    const { className, ...rest } = props;
    const merged = className ? `${moduleClass} ${className}` : moduleClass;
    return React.createElement(tag, { ...rest, ref, className: merged } as any);
  });
  Component.displayName = displayName;
  return Component;
}

// ---------------------------------------------------------------------------
// Page shell
// ---------------------------------------------------------------------------

export const PolicyPage = policyPrimitive<
  HTMLElement,
  React.ComponentPropsWithoutRef<'main'>
>('main', styles.policyPage, 'PolicyPage');

export const PolicyContainer = policyPrimitive<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<'div'>
>('div', styles.policyContainer, 'PolicyContainer');

// ---------------------------------------------------------------------------
// Header primitives
// ---------------------------------------------------------------------------

export const Eyebrow = policyPrimitive<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<'div'>
>('div', styles.eyebrow, 'Eyebrow');

export const PageTitle = policyPrimitive<
  HTMLHeadingElement,
  React.ComponentPropsWithoutRef<'h1'>
>('h1', styles.pageTitle, 'PageTitle');

export const PageLead = policyPrimitive<
  HTMLParagraphElement,
  React.ComponentPropsWithoutRef<'p'>
>('p', styles.pageLead, 'PageLead');

// ---------------------------------------------------------------------------
// Layout primitives
// ---------------------------------------------------------------------------

export const Divider = policyPrimitive<
  HTMLHRElement,
  React.ComponentPropsWithoutRef<'hr'>
>('hr', styles.divider, 'Divider');

export const Section = policyPrimitive<
  HTMLElement,
  React.ComponentPropsWithoutRef<'section'>
>('section', styles.section, 'Section');

// ---------------------------------------------------------------------------
// Content primitives
// ---------------------------------------------------------------------------

export const SectionTitle = policyPrimitive<
  HTMLHeadingElement,
  React.ComponentPropsWithoutRef<'h2'>
>('h2', styles.sectionTitle, 'SectionTitle');

export const SectionBody = policyPrimitive<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<'div'>
>('div', styles.sectionBody, 'SectionBody');

// ---------------------------------------------------------------------------
// Metadata card (dl)
// ---------------------------------------------------------------------------

export const MetaGrid = policyPrimitive<
  HTMLDListElement,
  React.ComponentPropsWithoutRef<'dl'>
>('dl', styles.metaGrid, 'MetaGrid');

// ---------------------------------------------------------------------------
// VDP-specific extras (kept here so the VDP page has a single import source)
// ---------------------------------------------------------------------------

export const TimelineTable = policyPrimitive<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<'div'>
>('div', styles.timelineTable, 'TimelineTable');

export const InlineLinkCard = policyPrimitive<
  HTMLAnchorElement,
  React.ComponentPropsWithoutRef<'a'>
>('a', styles.inlineLinkCard, 'InlineLinkCard');
